import type {
  ChecklistDocument,
  ChecklistStep,
} from '@midscene/test/checklist';
import { Alert, Button, Card, Form, Input, Modal, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import type { ChecklistState, TaskDetail } from '../shared/api';
import { ApiError, api } from './api';

const actions = [
  { value: 'launch', label: '启动应用 (launch)' },
  { value: 'home', label: '返回设备桌面 (home)' },
  { value: 'act', label: '界面操作 (act)' },
  { value: 'assert', label: '检查结果 (assert)' },
];
const required = [{ required: true, whitespace: true, message: '请填写内容' }];

export function ChecklistEditor({
  task,
  onClose,
  onSaved,
  onRefresh,
}: {
  task: TaskDetail;
  onClose: () => void;
  onSaved: (state: ChecklistState) => void;
  onRefresh: () => Promise<void>;
}) {
  const [base] = useState(task);
  const [form] = Form.useForm<ChecklistDocument>();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const close = () => {
    if (
      !saving &&
      (!dirty || window.confirm('清单修改尚未保存，确定放弃修改？'))
    ) {
      onClose();
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const state = await api.updateChecklist(base.id, {
        checklist: form.getFieldsValue(true),
        expectedChecklistRevision: base.checklistRevision,
      });
      onSaved(state);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '保存失败，请重试');
      setConflict(cause instanceof ApiError && cause.status === 409);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="编辑自动化用例清单"
      width={880}
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      onCancel={close}
      maskClosable={false}
      closable={!saving}
      keyboard={!saving}
      footer={
        <Space>
          <Button onClick={close} disabled={saving}>
            取消
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!dirty || conflict}
            onClick={() => form.submit()}
          >
            保存清单
          </Button>
        </Space>
      }
    >
      <p>
        保存后，下次 Studio 运行将使用新清单。home
        表示返回设备桌面，不是应用首页。
      </p>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
          action={
            conflict && (
              <Button
                onClick={async () => {
                  if (
                    dirty &&
                    !window.confirm('重新加载将放弃未保存的修改，是否继续？')
                  )
                    return;
                  try {
                    await onRefresh();
                    onClose();
                  } catch (cause) {
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : '重新加载失败，请重试',
                    );
                  }
                }}
              >
                重新加载
              </Button>
            )
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={base.checklist ?? undefined}
        onValuesChange={() => setDirty(true)}
        onFinish={save}
        disabled={saving}
      >
        <Form.List name="cases">
          {(cases, { add, remove }) => (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {cases.map((item) => (
                <Card
                  key={item.key}
                  size="small"
                  title={`用例 ${item.name + 1}`}
                  extra={
                    <Button
                      danger
                      disabled={cases.length === 1}
                      onClick={() => remove(item.name)}
                    >
                      删除用例
                    </Button>
                  }
                >
                  <Form.Item
                    name={[item.name, 'name']}
                    label="用例名称"
                    rules={required}
                  >
                    <Input />
                  </Form.Item>
                  <Form.List name={[item.name, 'steps']}>
                    {(steps, operations) => (
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                      >
                        {steps.map((step) => (
                          <div
                            key={step.key}
                            style={{
                              borderTop: '1px solid #e2e8f0',
                              paddingTop: 12,
                            }}
                          >
                            <Space wrap style={{ marginBottom: 8 }}>
                              <strong>步骤 {step.name + 1}</strong>
                              <Button
                                size="small"
                                disabled={step.name === 0}
                                onClick={() =>
                                  operations.move(step.name, step.name - 1)
                                }
                              >
                                上移
                              </Button>
                              <Button
                                size="small"
                                disabled={step.name === steps.length - 1}
                                onClick={() =>
                                  operations.move(step.name, step.name + 1)
                                }
                              >
                                下移
                              </Button>
                              <Button
                                size="small"
                                danger
                                disabled={steps.length === 1}
                                onClick={() => operations.remove(step.name)}
                              >
                                删除步骤
                              </Button>
                            </Space>
                            <Form.Item
                              name={[step.name, 'action']}
                              label="步骤类型"
                              rules={[{ required: true }]}
                            >
                              <Select options={actions} />
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate>
                              {() => {
                                const action = form.getFieldValue([
                                  'cases',
                                  item.name,
                                  'steps',
                                  step.name,
                                  'action',
                                ]) as ChecklistStep['action'];
                                if (action === 'home') return null;
                                const field =
                                  action === 'launch'
                                    ? 'app'
                                    : action === 'assert'
                                      ? 'expected'
                                      : 'intent';
                                const label =
                                  action === 'launch'
                                    ? '应用名称或包名'
                                    : action === 'assert'
                                      ? '预期结果'
                                      : '操作描述';
                                return (
                                  <Form.Item
                                    key={field}
                                    name={[step.name, field]}
                                    label={label}
                                    rules={required}
                                  >
                                    <Input.TextArea
                                      autoSize={{ minRows: 2, maxRows: 6 }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </div>
                        ))}
                        <Button
                          onClick={() =>
                            operations.add({ action: 'act', intent: '' })
                          }
                        >
                          新增步骤
                        </Button>
                      </Space>
                    )}
                  </Form.List>
                </Card>
              ))}
              <Button
                block
                onClick={() =>
                  add({
                    id: crypto.randomUUID(),
                    name: '',
                    steps: [{ action: 'act', intent: '' }],
                  })
                }
              >
                新增用例
              </Button>
            </Space>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}
